/**
 * The invitation token that survives the sign-in round trip (ORDR-61).
 *
 * Verify is anonymous and happens on `/invite/<token>/verify`. Accept is not:
 * paio-auth matches the SIGNED-IN email against the invited one, so it can only
 * happen after the provider round trip, in `screens/AuthCallback.tsx`. Between
 * the two the browser leaves the origin entirely, so the token is put down here
 * and picked up there.
 *
 * WHY NOT `next=`. `safeReturnPath` refuses any address that is itself a
 * sign-in screen, and the invitation address became one when `authScreenFor`
 * learned it. That refusal is correct, because returning to a spent invitation
 * after sign-in would loop, and it is also why the token cannot ride in the
 * return path.
 *
 * STORAGE IS NOT A TRUST BOUNDARY, exactly as `returnPath.ts` says of `next=`.
 * Anything sharing the origin can write here, so what comes back is validated
 * against the same shape a real token has before it is put in a URL and sent to
 * the server. A value that fails is dropped, not repaired.
 *
 * EVERY ACCESS IS WRAPPED. Safari in private mode throws on `setItem`, and some
 * configurations throw on read. A sign-in that otherwise worked must not fail
 * because storage is switched off; the cost of the failure is an invitation
 * that is verified but not accepted, which is recoverable, and an exception
 * escaping here would strand the browser on the callback, which is not.
 */

const STORAGE_KEY = "paio.ordering.pendingInvitation";

/** Matches `paths.ts`: a token is opaque and URL-safe, never a path. */
const TOKEN = /^[A-Za-z0-9._~%-]+$/;

/** Longer than any token paio-auth issues, short enough to bound a bad write. */
const MAX_LENGTH = 512;

function plausible(value: string | null | undefined): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_LENGTH &&
    TOKEN.test(value)
  );
}

/** Put the token down before leaving for the provider. */
export function writePendingInvitation(token: string): void {
  // Refuse to store what `readPendingInvitation` would refuse to return, so a
  // bad value fails here rather than silently going missing later.
  if (!plausible(token)) {
    return;
  }
  try {
    window.sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Storage switched off. The accept will not happen; sign-in still will.
  }
}

/** The token to accept after signing in, or `null` if there is none to trust. */
export function readPendingInvitation(): string | null {
  let stored: string | null;
  try {
    stored = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  return plausible(stored) ? stored : null;
}

/** Drop the token, so a later sign-in does not replay a spent one. */
export function clearPendingInvitation(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
